import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, auth as Auth, ApiError, type Me } from '@tvx/api-client';
import { NavIcon, GroupNavIcon } from './components/NavIcon';
import { LuLogOut } from 'react-icons/lu';
import { ThemeToggle } from './components/ThemeToggle';
import { Dashboard } from './screens/Dashboard';
import { Members } from './screens/Members';
import { Payments } from './screens/Payments';
import { Pages } from './screens/Pages';
import { Legal } from './screens/Legal';
import { Faqs } from './screens/Faqs';
import { Testimonials } from './screens/Testimonials';
import { Chrome } from './screens/Chrome';
import { Shim } from './components/Shimmer';
import { Library } from './screens/Library';
import { Categories } from './screens/Categories';
import { TradeCalls } from './screens/TradeCalls';
import { Sotd } from './screens/Sotd';
import { Learning } from './screens/Learning';
import { Batches } from './screens/Batches';
import { Ads } from './screens/Ads';
import { Notifications } from './screens/Notifications';
import { Enquiries } from './screens/Enquiries';
import { LemonRequests } from './screens/LemonRequests';
import { MarketCalendar } from './screens/MarketCalendar';
import { MarketNewsAdmin } from './screens/MarketNewsAdmin';
import { MarketMoversAdmin } from './screens/MarketMoversAdmin';
import { Passes } from './screens/Passes';
import { Roles } from './screens/Roles';
import { Team } from './screens/Team';
import { Settings } from './screens/Settings';
import { Email } from './screens/Email';
import { MobileOtp } from './screens/MobileOtp';
import { NewsFeeds } from './screens/NewsFeeds';
import { Backups } from './screens/Backups';
import { Modules } from './screens/Modules';
import { ArticleEditor } from './screens/ArticleEditor';
import { Login } from './screens/Login';

type Item = {
  to: string;
  label: string;
  /** The permission the server requires. */
  perm: string;
  element: ReactNode;
  /** Routes with no rail item of their own that this item stands in for. */
  owns?: string[];
};
type Group = { key: string; label: string; items: Item[] };

/**
 * The rail, and the routes, from one list.
 *
 * They used to be two: a menu array and a hand-written <Route> per screen,
 * each naming its own permission. Nothing checked that the pair agreed, and a
 * menu entry guarding `users` in front of a screen guarding `pay` is a bug you
 * only find by holding the wrong role. One list means a screen cannot be
 * listed and unrouted, routed and unlisted, or guarded two ways.
 *
 * The grouping is the console's own modules, and matches the old console's
 * rail — Overview, Publishing, Operations, Website, Settings — so nobody has
 * to relearn where a screen lives.
 */
const NAV: Group[] = [
  {
    key: 'overview',
    label: 'Overview',
    items: [
      { to: '/dashboard', label: 'Dashboard', perm: 'dash', element: <Dashboard /> },
      { to: '/members', label: 'Members', perm: 'users', element: <Members /> },
      { to: '/payments', label: 'Payments', perm: 'pay', element: <Payments /> },
    ],
  },
  /* The client's four modules, one group each. A module switched off under
     Settings → Modules takes its permissions with it, so its whole group
     drops out of this list — nothing here has to know about the switch. */
  {
    key: 'learning',
    label: 'Learning',
    items: [
      { to: '/recorded-learning', label: 'Recorded Learning', perm: 'library', element: <Learning /> },
      { to: '/live-batches', label: 'Live Batches', perm: 'batch', element: <Batches /> },
    ],
  },
  {
    key: 'news',
    label: 'News & Analysis',
    items: [
      /* The article editor is a route, not a rail item — while it is open the
         library it came from is what should look current. */
      { to: '/library', label: 'Articles', perm: 'article', element: <Library />, owns: ['/articles'] },
      { to: '/categories', label: 'Categories', perm: 'article', element: <Categories /> },
      { to: '/stocks-of-the-day', label: 'Stocks Of The Day', perm: 'sotd', element: <Sotd /> },
      { to: '/market-news', label: 'Market News', perm: 'content', element: <MarketNewsAdmin /> },
      { to: '/market-movers', label: 'Market Movers', perm: 'content', element: <MarketMoversAdmin /> },
      { to: '/market-calendar', label: 'Market Calendar', perm: 'events', element: <MarketCalendar /> },
      { to: '/news-plans', label: 'Plans', perm: 'setPlans.news', element: <Passes module="news" /> },
    ],
  },
  {
    key: 'calls',
    label: 'Trade Calls',
    items: [
      { to: '/trade-calls', label: 'Trade Calls', perm: 'newcall', element: <TradeCalls /> },
      { to: '/trade-call-plans', label: 'Plans', perm: 'setPlans.calls', element: <Passes module="calls" /> },
    ],
  },
  {
    key: 'broker',
    label: 'Broker Referral',
    items: [
      { to: '/ads', label: 'Broker & Ads', perm: 'ads', element: <Ads /> },
      { to: '/lemon', label: 'Telegram pass', perm: 'users', element: <LemonRequests /> },
    ],
  },
  {
    key: 'audience',
    label: 'Audience',
    items: [
      { to: '/notifications', label: 'Notifications', perm: 'notif', element: <Notifications /> },
      { to: '/enquiries', label: 'Enquiries', perm: 'leads', element: <Enquiries /> },
    ],
  },
  {
    key: 'website',
    label: 'Website',
    items: [
      { to: '/chrome', label: 'Header & footer', perm: 'website', element: <Chrome /> },
      { to: '/pages', label: 'Landing', perm: 'website', element: <Pages /> },
      { to: '/faq', label: 'FAQ', perm: 'website', element: <Faqs /> },
      { to: '/testimonials', label: 'Testimonials', perm: 'website', element: <Testimonials /> },
      { to: '/legal', label: 'Legal pages', perm: 'website', element: <Legal /> },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    items: [
      { to: '/settings/modules', label: 'Modules', perm: 'settings', element: <Modules /> },
      { to: '/settings', label: 'Contact & Business', perm: 'settings', element: <Settings /> },
      { to: '/settings/email', label: 'Email accounts', perm: 'setEmail', element: <Email /> },
      { to: '/settings/mobile-otp', label: 'Mobile & OTP', perm: 'setSms', element: <MobileOtp /> },
      { to: '/settings/news-feeds', label: 'News feeds', perm: 'settings', element: <NewsFeeds /> },
      { to: '/settings/backups', label: 'Backups', perm: 'setBack', element: <Backups /> },
      { to: '/team', label: 'Team', perm: 'setTeam', element: <Team /> },
      { to: '/roles', label: 'Roles & permissions', perm: 'setRoles', element: <Roles /> },
    ],
  },
];

/* Routed, deliberately not in the rail: reached from the screen that owns it. */
const DEEP: Item[] = [
  { to: '/articles/:id', label: 'Article', perm: 'article', element: <ArticleEditor /> },
];

const ALL_ITEMS = NAV.flatMap((g) => g.items).concat(DEEP);

/* Which groups are shut, remembered between visits. Collapsed rather than open
   is what is stored, so a group added later starts open instead of hiding
   itself from everyone who has used the console before. */
const SHUT_KEY = 'tvx.console.nav.shut';

function readShut(): Set<string> {
  try {
    const raw = localStorage.getItem(SHUT_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/* Whether the rail itself is collapsed to a strip. Kept apart from the
   per-section state above: shutting a section is about tidying the menu,
   collapsing the rail is about giving a wide table the width it wants. */
const RAIL_KEY = 'tvx.console.nav.rail';

function readRailShut(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === 'shut';
  } catch {
    return false;
  }
}

/** Does this rail item stand for the page being shown? */
function holds(item: Item, path: string): boolean {
  if (path === item.to) return true;
  return (item.owns ?? []).some((p) => path === p || path.startsWith(`${p}/`));
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [railShut, setRailShut] = useState<boolean>(readRailShut);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    try { localStorage.setItem(RAIL_KEY, railShut ? 'shut' : 'open'); } catch { /* private mode */ }
  }, [railShut]);

  const load = useCallback(() => {
    setAuthError(null);
    api.get<Me>('/auth/me')
      .then(setMe)
      .catch((e: ApiError) => setAuthError(e.isUnauthenticated ? 'signin' : e.message));
  }, []);

  useEffect(load, [load]);

  /* Signing out drops the local copy of who this is as well as the cookie.
     Reloading instead would leave the previous person's name on screen for as
     long as the round trip takes — on a shared desk that is the wrong thing to
     show. Even if the request fails, the session is treated as over here. */
  const signOut = useCallback(async () => {
    try { await Auth.logout(); } finally {
      setMe(null);
      setAuthError('signin');
    }
  }, []);

  /* Until /auth/me answers, nothing is known about what this person may open —
     so the menu is empty rather than briefly offering everything and then
     taking half of it away. */
  const groups = useMemo(() => {
    if (!me) return [];
    const held = new Set(me.permissions);
    return NAV
      .map((g) => ({ ...g, items: g.items.filter((i) => held.has(i.perm)) }))
      .filter((g) => g.items.length > 0);
  }, [me]);
  const first = groups[0]?.items[0]?.to ?? '/dashboard';

  async function stopViewAs() {
    try {
      await api.post('/auth/stop-view-as');
      load();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  /* Signed out — including a session that expired while a screen was open, so
     this is reached from anywhere rather than only on first load. */
  if (authError === 'signin') return <Login onSignedIn={load} />;
  if (!me && !authError) {
    return (
      <div style={{ padding: 40, display: 'grid', gap: 12, maxWidth: 420 }}>
        <Shim w={180} h={20} /><Shim w="86%" /><Shim w="62%" />
      </div>
    );
  }

  return (
    <div className={`shell${railShut ? ' rail-shut' : ''}${mobileNavOpen ? ' nav-open' : ''}`}>
      <header className="admin-mhead">
        <button
          type="button"
          className="admin-burger"
          aria-label="Open menu"
          onClick={() => setMobileNavOpen(true)}
        >
          <span /><span /><span />
        </button>
        <div className="admin-mbrand">
          <img src="/assets/tradevix-mark.png" alt="" />
          <b>Tradevix</b><span>Console</span>
        </div>
        {me ? (
          <div className="admin-muser">
            <span>{formatAdminName(me.user.name).split(' ')[0]}</span>
          </div>
        ) : <div style={{ width: 32 }} />}
      </header>
      <div className="nav-scrim" onClick={() => setMobileNavOpen(false)} />
      <Rail
        groups={groups}
        me={me}
        onSignOut={signOut}
        onStopViewAs={stopViewAs}
        shutRail={railShut}
        onToggleRail={() => setRailShut((v) => !v)}
        onCloseMobileNav={() => setMobileNavOpen(false)}
      />
      <main>
        {authError && authError !== 'signin' ? <div className="err">{authError}</div> : null}
        <Routes>
          <Route path="/" element={<Navigate to={first} replace />} />
          {ALL_ITEMS.map((item) => (
            <Route
              key={item.to}
              path={item.to}
              element={<Guard perm={item.perm} me={me}>{item.element}</Guard>}
            />
          ))}
          <Route path="*" element={<div className="crd" style={{ padding: 40 }}>
            No such screen. <NavLink to={first}>Back to the console</NavLink>.
          </div>} />
        </Routes>
      </main>
    </div>
  );
}

/**
 * The left rail: one collapsible section per module.
 *
 * Two rules keep collapsing from ever hiding where you are. The section
 * holding the current screen is opened when you arrive in it — you can reach a
 * screen from a link or a bookmark, not only from the rail. And a section you
 * shut while it holds the current screen keeps a marker on its header, so the
 * rail never looks as though nothing is selected.
 */
function formatAdminName(name?: string) {
  if (!name) return '';
  return name.replace(/\s*\([^)]*\)/g, '').trim() || name;
}

function Rail({ groups, me, onSignOut, onStopViewAs, shutRail, onToggleRail, onCloseMobileNav }: {
  groups: Group[];
  me: Me | null;
  onSignOut: () => void | Promise<void>;
  onStopViewAs: () => void;
  shutRail: boolean;
  onToggleRail: () => void;
  onCloseMobileNav?: () => void;
}) {
  const { pathname } = useLocation();
  const [shut, setShut] = useState<Set<string>>(readShut);
  const adminName = me?.user ? formatAdminName(me.user.name) : '';

  const current = groups.find((g) => g.items.some((i) => holds(i, pathname)));

  useEffect(() => {
    if (!current) return;
    setShut((s) => (s.has(current.key) ? new Set([...s].filter((k) => k !== current.key)) : s));
  }, [current?.key]);          // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try { localStorage.setItem(SHUT_KEY, JSON.stringify([...shut])); } catch { /* private mode */ }
  }, [shut]);

  const toggle = (key: string) => setShut((s) => {
    const next = new Set(s);
    if (!next.delete(key)) next.add(key);
    return next;
  });

  return (
    <nav id="console-rail">
      <div className="brand">
        <img src="/assets/tradevix-mark.png" alt="" />
        <div className="brand-name"><b>Tradevix</b><span>Console</span></div>
        {/* The one control that survives collapsing. A rail that can be shut
            with no visible way to open it again is a rail nobody shuts twice. */}
        <button
          type="button"
          className="railtog"
          aria-expanded={!shutRail}
          aria-controls="console-rail"
          title={shutRail ? 'Expand the menu' : 'Collapse the menu'}
          onClick={onToggleRail}
        >
          <span className="vh">{shutRail ? 'Expand the menu' : 'Collapse the menu'}</span>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2.5" />
            <path d="M9.5 4v16" />
          </svg>
        </button>
        <button
          type="button"
          className="rail-close-btn"
          aria-label="Close menu"
          onClick={onCloseMobileNav}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="rail-groups">
        {groups.map((g) => {
          const open = !shut.has(g.key);
          const here = current?.key === g.key;
          return (
            <div className={`nvgrp${open ? ' is-open' : ''}${here ? ' is-here' : ''}`} key={g.key}>
              <button
                type="button"
                className="nvgrp-head"
                aria-expanded={open}
                aria-controls={`nvgrp-${g.key}`}
                title={g.label}
                onClick={() => toggle(g.key)}
              >
                <GroupNavIcon groupKey={g.key} className="nvgrp-icon" />
                <span className="lb">{g.label}</span>
                <span className="nvgrp-count">{g.items.length}</span>
                <svg className="nvgrp-caret" width="11" height="11" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                  aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
              </button>
              {/* Shut sections stay in the document so the height can animate, and
                  are inert so tabbing never lands on a link nobody can see. */}
              <div className="nvsub-wrap" id={`nvgrp-${g.key}`} inert={open && !shutRail ? undefined : true}>
                <div className="nvsub-list">
                  {g.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end
                      title={item.label}
                      className={({ isActive }) => `nv nvsub${isActive || holds(item, pathname) ? ' is-on' : ''}`}
                      onClick={onCloseMobileNav}
                    >
                      <NavIcon to={item.to} className="nv-icon" />
                      <span className="lb">{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {me ? (
        <div className="whoami">
          {me.impersonating && !shutRail ? (
            <div style={{ marginBottom: 10, padding: '6px 8px', background: 'rgba(var(--goldrgb), 0.12)', border: '1px solid var(--gold)', borderRadius: 8, fontSize: 11 }}>
              <div style={{ color: 'var(--goldtx)', fontWeight: 700 }}>Viewing as {adminName}</div>
              <button
                type="button"
                className="b bg bs"
                style={{ padding: '3px 8px', fontSize: 10, marginTop: 5, width: '100%', textAlign: 'center' }}
                onClick={onStopViewAs}
              >
                Stop viewing as
              </button>
            </div>
          ) : null}
          {/* Beside the person, not buried in a settings screen: it is a
              preference about this screen, changed while looking at it. */}
          <div className={`whoami-theme${shutRail ? ' is-compact' : ''}`}>
            <ThemeToggle compact={shutRail} />
          </div>
          {shutRail ? (
            <div className="whoami-collapsed" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div
                className="user-avatar"
                title={`${adminName} (${me.user.roleName ?? (me.user.isOwner ? 'Owner' : 'Staff')})`}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'var(--goldsoft)',
                  color: 'var(--goldtx)',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                {adminName.charAt(0).toUpperCase()}
              </div>
              <button
                type="button"
                className="b bt bs"
                style={{
                  width: 32,
                  height: 32,
                  padding: 0,
                  display: 'grid',
                  placeItems: 'center',
                  color: 'var(--down)',
                  background: 'rgba(240,100,91,0.08)',
                  border: '1px solid rgba(240,100,91,0.25)',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
                title="Sign out of admin console"
                onClick={onSignOut}
                aria-label="Sign out"
              >
                <LuLogOut size={14} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--tx)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12.5 }}>
                  {adminName}
                </div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                  {me.user.roleName ?? (me.user.isOwner ? 'Owner' : 'Staff')}
                </div>
              </div>
              <button
                type="button"
                className="b bt bs"
                style={{
                  padding: '4px 8px',
                  fontSize: 11,
                  color: 'var(--down)',
                  background: 'rgba(240,100,91,0.08)',
                  border: '1px solid rgba(240,100,91,0.25)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
                title="Sign out of admin console"
                onClick={onSignOut}
              >
                <LuLogOut size={12} />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      ) : null}
    </nav>
  );
}

/**
 * A screen the signed-in role does not grant.
 *
 * The server refuses it regardless — this only decides what the person reads
 * while being refused. "Ask an Owner" is more use than a 403.
 */
function Guard({ perm, me, children }: { perm: string; me: Me | null; children: ReactNode }) {
  if (!me) return <Shim w="55%" h={16} />;
  if (me.permissions.includes(perm)) return <>{children}</>;
  return (
    <div className="crd" style={{ padding: '40px 32px', textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--fd)', fontSize: 19, marginBottom: 8 }}>Not available on your role</div>
      <p className="muted" style={{ fontSize: 13.5 }}>
        Your role does not include this screen. An Owner can add it under Settings → Roles.
      </p>
    </div>
  );
}
